"use client";

import { useState } from "react";
import Image from "@/components/OptimizedImage";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { BlogPost } from "@/lib/item-reader";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface BlogSectionProps {
  title?: string;
  artPosts?: BlogPost[];
  workPosts?: BlogPost[];
  maxPosts?: number;
}

function getCleanSlug(slug: string): string {
  const parts = slug.split('/');
  return parts[parts.length - 1];
}

function getPostLink(post: BlogPost, isWorkPost: boolean): string {
  if (isWorkPost) {
    return `/work/post/${getCleanSlug(post.slug)}`;
  }
  return `/art/${post.slug}`;
}

export default function BlogSection({ title = "Latest", artPosts = [], workPosts = [], maxPosts = 6 }: BlogSectionProps) {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  const handleImageLoad = (src: string) => {
    setLoadedImages(prev => new Set(prev).add(src));
  };

  // Combine all posts and sort by date (most recent first)
  const combined = [
    ...workPosts.map(post => ({ post, isWork: true })),
    ...artPosts.map(post => ({ post, isWork: false })),
  ].sort((a, b) => {
    const dateA = a.post.date ? new Date(a.post.date).getTime() : 0;
    const dateB = b.post.date ? new Date(b.post.date).getTime() : 0;
    return dateB - dateA;
  });

  const postsToShow = combined.slice(0, maxPosts);

  return (
    <section className="section-padding container">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-3xl font-medium tracking-tighter md:text-4xl">
          {title}
        </h2>
        <Link
          href="/art"
          className="text-sm font-medium hover:underline flex items-center gap-1"
        >
          View all
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {postsToShow.map(({ post, isWork }) => {
          const imageSrc = post.coverImage || post.image || "/placeholder.jpg";
          const isLoaded = loadedImages.has(imageSrc);

          return (
            <Link
              key={`${isWork ? 'work' : 'art'}-${post.slug}`}
              href={getPostLink(post, isWork)}
              className="group block h-full"
            >
              <Card className="group h-full gap-4 overflow-hidden rounded-2xl py-5 shadow-none transition-all duration-200 border-0">
                <CardHeader className="px-5">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
                    {/* Skeleton placeholder */}
                    {!isLoaded && (
                      <Skeleton className="absolute inset-0 z-10 rounded-xl" />
                    )}
                    <Image
                      src={imageSrc}
                      alt={post.title}
                      fill
                      className={cn(
                        "object-cover transition-all duration-300 group-hover:scale-102 rounded-xl",
                        isLoaded ? "opacity-100" : "opacity-0"
                      )}
                      onLoad={() => handleImageLoad(imageSrc)}
                    />
                  </div>
                </CardHeader>
                <CardContent className="mb-8 px-5">
                  <h3 className="text-xl">{post.title}</h3>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-muted-foreground line-clamp-3">
                      {post.description}
                    </span>
                  </div>
                </CardContent>
                <CardFooter className="mt-auto mb-0 flex flex-wrap items-center justify-between gap-2 justify-self-end px-5">
                  <span className="text-foreground text-sm font-medium group-hover:underline">
                    Explore the series
                  </span>
                </CardFooter>
              </Card>
            </Link>
          );
        })}
      </div>
    </section>
  );
}